import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useProfile } from "@/lib/profile/useProfile";
import { TIER_LABELS, type FriendshipLane, type ProfilePrompt } from "@/lib/profile/types";
import { APPS, type AppId, type LiveAppId } from "@/lib/apps";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PhotoGrid from "@/components/profile/PhotoGrid";
import PromptsPicker from "@/components/profile/PromptsPicker";
import VoiceRecorder from "@/components/profile/VoiceRecorder";
import EmergencyButtonSettings from "@/components/safety/EmergencyButtonSettings";
import AutoSafetyShareToggle from "@/components/safety/AutoSafetyShareToggle";
import KycPanel from "@/components/safety/KycPanel";
import TrustedCirclePanel from "@/components/safety/TrustedCirclePanel";
import SafetyCheckinPanel from "@/components/safety/SafetyCheckinPanel";
import WhoCanSeeMePanel from "@/components/safety/WhoCanSeeMePanel";
import PreMeetChecklistPanel from "@/components/safety/PreMeetChecklistPanel";
import TrustRestrictionMatrix from "@/components/trust/TrustRestrictionMatrix";
import NotificationPreferencesPanel from "@/components/notifications/NotificationPreferencesPanel";
import DsarPanel from "@/components/compliance/DsarPanel";
import RefundsPanel from "@/components/billing/RefundsPanel";
import LocalePanel from "@/components/i18n/LocalePanel";
import MilestoneList from "@/components/milestones/MilestoneList";
import CrossAppSyncPanel from "@/components/account/CrossAppSyncPanel";
import { EnablePushButton } from "@/components/notifications/EnablePushButton";
import ProfileQrCard from "@/components/profile/ProfileQrCard";
import ProfileCompletionMeter from "@/components/profile/ProfileCompletionMeter";
import BgcReverifyBanner from "@/components/safety/BgcReverifyBanner";
import StrikeHistoryPanel from "@/components/safety/StrikeHistoryPanel";
import StreakRecoveryCard from "@/components/streaks/StreakRecoveryCard";
import UpcomingRemindersCard from "@/components/engagement/UpcomingRemindersCard";
import DailySignalNudge from "@/components/engagement/DailySignalNudge";
import AnnualUpsellCard from "@/components/billing/AnnualUpsellCard";
import AlacarteCrossSell from "@/components/billing/AlacarteCrossSell";
import CommunityAddOnUpsell from "@/components/billing/CommunityAddOnUpsell";
import { useStreak } from "@/lib/streaks/useStreak";
import { Flame } from "lucide-react";
import { toast } from "sonner";

type UsernameState = "idle" | "checking" | "available" | "taken" | "invalid" | "unchanged";

const USERNAME_RE = /^[a-z0-9_]{3,24}$/;

const Account = () => {
  const { user, signOut } = useAuth();
  const { profile, loading, update } = useProfile();
  const { streak } = useStreak();
  const [busy, setBusy] = useState(false);

  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [usernameState, setUsernameState] = useState<UsernameState>("idle");
  const [legalFirstName, setLegalFirstName] = useState("");
  const [legalLastName, setLegalLastName] = useState("");
  const [pronouns, setPronouns] = useState("");
  const [bio, setBio] = useState("");
  const [locationText, setLocationText] = useState("");
  const [activeApp, setActiveApp] = useState<AppId | "">("");
  const [emergencyName, setEmergencyName] = useState("");
  const [emergencyPhone, setEmergencyPhone] = useState("");
  const [aiOptIn, setAiOptIn] = useState(false);
  const [incognito, setIncognito] = useState(false);
  const [photos, setPhotos] = useState<string[]>([]);
  const [prompts, setPrompts] = useState<ProfilePrompt[]>([]);
  const [voiceUrl, setVoiceUrl] = useState<string | null>(null);
  const [lane, setLane] = useState<FriendshipLane>("dating");

  const checkTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!profile) return;
    setDisplayName(profile.display_name ?? "");
    setUsername(profile.username ?? "");
    setLegalFirstName(profile.legal_first_name ?? "");
    setLegalLastName(profile.legal_last_name ?? "");
    setPronouns(profile.pronouns ?? "");
    setBio(profile.bio ?? "");
    setLocationText(profile.location_text ?? "");
    setActiveApp((profile.active_app as AppId) ?? "");
    setEmergencyName(profile.emergency_contact_name ?? "");
    setEmergencyPhone(profile.emergency_contact_phone ?? "");
    setAiOptIn(profile.ai_training_opt_in);
    setIncognito(profile.incognito);
    setPhotos(profile.photos ?? []);
    setPrompts(profile.prompts ?? []);
    setVoiceUrl(profile.voice_intro_url);
    setLane(profile.friendship_lane ?? "dating");
  }, [profile]);

  // Live username availability (debounced 400ms)
  useEffect(() => {
    if (!profile) return;
    const trimmed = username.trim().toLowerCase();
    if (trimmed === (profile.username ?? "").toLowerCase()) {
      setUsernameState("unchanged");
      return;
    }
    if (trimmed === "") {
      setUsernameState("idle");
      return;
    }
    if (!USERNAME_RE.test(trimmed)) {
      setUsernameState("invalid");
      return;
    }
    setUsernameState("checking");
    if (checkTimer.current) window.clearTimeout(checkTimer.current);
    checkTimer.current = window.setTimeout(async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id")
        .eq("username", trimmed)
        .maybeSingle();
      if (error) {
        setUsernameState("idle");
        return;
      }
      if (!data || data.id === profile.id) setUsernameState("available");
      else setUsernameState("taken");
    }, 400);
    return () => {
      if (checkTimer.current) window.clearTimeout(checkTimer.current);
    };
  }, [username, profile]);

  const legalNameChanged =
    !!profile &&
    (legalFirstName.trim() !== (profile.legal_first_name ?? "") ||
      legalLastName.trim() !== (profile.legal_last_name ?? ""));
  const wasKycVerified = profile?.kyc_status === "verified";

  const save = async () => {
    if (usernameState === "taken") {
      toast.error("That username is already taken.");
      return;
    }
    if (usernameState === "invalid") {
      toast.error("Username must be 3–24 chars: lowercase letters, numbers, underscores.");
      return;
    }
    if (usernameState === "checking") {
      toast.error("Still checking that username — try again in a moment.");
      return;
    }
    if (legalNameChanged && (!legalFirstName.trim() || !legalLastName.trim())) {
      toast.error("Legal first and last name cannot be empty.");
      return;
    }
    setBusy(true);
    const patch: Record<string, unknown> = {
      display_name: displayName || null,
      username: username.trim().toLowerCase() || null,
      legal_first_name: legalFirstName.trim() || null,
      legal_last_name: legalLastName.trim() || null,
      pronouns: pronouns || null,
      bio: bio || null,
      location_text: locationText || null,
      active_app: activeApp || null,
      emergency_contact_name: emergencyName || null,
      emergency_contact_phone: emergencyPhone || null,
      ai_training_opt_in: aiOptIn,
      incognito,
      photos,
      prompts: prompts.filter((p) => p.answer.trim()),
      voice_intro_url: voiceUrl,
      friendship_lane: lane,
    };
    // If legal name changed after KYC, force re-verification
    if (legalNameChanged && wasKycVerified) {
      patch.kyc_status = "unverified";
      patch.kyc_verified_at = null;
    }
    const { error } = await update(patch);
    setBusy(false);
    if (error) {
      // Postgres unique violation = 23505
      if (error.toLowerCase().includes("duplicate") || error.includes("23505")) {
        toast.error("That username is already taken.");
        setUsernameState("taken");
      } else {
        toast.error(error);
      }
      return;
    }
    if (legalNameChanged && wasKycVerified) {
      toast.success("Profile updated. ID verification has been reset — please re-verify.");
    } else {
      toast.success("Profile updated.");
    }
  };

  const isAttuned = profile?.tier === "tier_attuned";

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <main className="flex-1 mx-auto w-full max-w-3xl px-6 py-16">
        <p className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">
          Account
        </p>
        <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
          <h1 className="text-4xl font-bold tracking-tight">
            {profile?.display_name || "Welcome"}
          </h1>
          {profile && (
            <span className="inline-flex items-center rounded-full border border-border px-3 py-1 text-xs font-medium">
              {TIER_LABELS[profile.tier]}
            </span>
          )}
        </div>
        <p className="mt-3 text-muted-foreground">
          Signed in as <span className="text-foreground font-medium">{user?.email}</span>.
        </p>

        <ProfileCompletionMeter />
        <div className="mt-4 space-y-4">
          <DailySignalNudge />
          <StreakRecoveryCard />
          <BgcReverifyBanner />
          <UpcomingRemindersCard />
        </div>

        {loading ? (
          <p className="mt-10 text-sm text-muted-foreground">Loading profile…</p>
        ) : (
          <>
            {/* Public identity */}
            <section className="mt-10 rounded-2xl border border-border p-6 bg-card space-y-4">
              <div>
                <h2 className="text-lg font-semibold">Public profile</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  This is what other members see.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="dn">Display name</Label>
                  <Input id="dn" maxLength={50} value={displayName}
                         onChange={(e) => setDisplayName(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="un">Username</Label>
                  <Input
                    id="un"
                    value={username}
                    maxLength={24}
                    onChange={(e) => setUsername(e.target.value)}
                    aria-invalid={usernameState === "taken" || usernameState === "invalid"}
                  />
                  <p className={`mt-1 text-xs ${
                    usernameState === "available" ? "text-success"
                      : usernameState === "taken" || usernameState === "invalid" ? "text-destructive"
                      : "text-muted-foreground"
                  }`}>
                    {usernameState === "checking" && "Checking availability…"}
                    {usernameState === "available" && "Available."}
                    {usernameState === "taken" && "Taken — try another."}
                    {usernameState === "invalid" && "3–24 chars: lowercase letters, numbers, underscores."}
                    {(usernameState === "idle" || usernameState === "unchanged") &&
                      "3–24 chars: lowercase letters, numbers, underscores."}
                  </p>
                </div>
                <div>
                  <Label htmlFor="pn">Pronouns</Label>
                  <Input id="pn" maxLength={50} value={pronouns}
                         onChange={(e) => setPronouns(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="loc">Location</Label>
                  <Input id="loc" maxLength={120} value={locationText}
                         onChange={(e) => setLocationText(e.target.value)} />
                </div>
              </div>
              <div>
                <Label htmlFor="bio">Bio</Label>
                <Textarea id="bio" rows={4} maxLength={500} value={bio}
                          onChange={(e) => setBio(e.target.value)} />
              </div>
              {profile?.profile_id && (
                <p className="text-xs text-muted-foreground">
                  Public link:{" "}
                  <Link
                    to={`/u/${profile.profile_id}`}
                    className="underline hover:text-foreground"
                  >
                    /u/{profile.profile_id}
                  </Link>
                </p>
              )}
            </section>

            {/* Photos */}
            <section className="mt-6 rounded-2xl border border-border p-6 bg-card space-y-4">
              <div>
                <h2 className="text-lg font-semibold">Photos</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Drag to reorder. First photo is your main.
                </p>
              </div>
              <PhotoGrid value={photos} onChange={setPhotos} />
            </section>

            {/* Prompts */}
            {profile?.primary_app && (
              <section className="mt-6 rounded-2xl border border-border p-6 bg-card space-y-4">
                <div>
                  <h2 className="text-lg font-semibold">Prompts</h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Show your personality. Up to 8.
                  </p>
                </div>
                <PromptsPicker
                  app={profile.primary_app as LiveAppId}
                  value={prompts}
                  onChange={setPrompts}
                />
              </section>
            )}

            {/* Voice intro */}
            <section className="mt-6 rounded-2xl border border-border p-6 bg-card space-y-4">
              <div>
                <h2 className="text-lg font-semibold">Voice intro</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Optional. Up to 30 seconds.
                </p>
              </div>
              <VoiceRecorder value={voiceUrl} onChange={setVoiceUrl} />
            </section>

            {/* Friendship lane */}
            <section className="mt-6 rounded-2xl border border-border p-6 bg-card space-y-4">
              <div>
                <h2 className="text-lg font-semibold">What you're here for</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Affects who you see and who sees you.
                </p>
              </div>
              <Select value={lane} onValueChange={(v) => setLane(v as FriendshipLane)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dating">Dating</SelectItem>
                  <SelectItem value="friendship">Friendship</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                  <SelectItem value="mentorship">Mentorship</SelectItem>
                </SelectContent>
              </Select>
            </section>

            {/* Milestones & streak */}
            <section className="mt-6 rounded-2xl border border-border p-6 bg-card space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold">Milestones & streak</h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    What you've earned. Streak counts every day you open the app.
                  </p>
                </div>
                {streak && streak.current > 0 && (
                  <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1.5 text-xs">
                    <Flame className="h-3.5 w-3.5 text-primary" aria-hidden />
                    <span className="font-medium">{streak.current}-day streak</span>
                    <span className="text-muted-foreground">· longest {streak.longest}</span>
                  </div>
                )}
              </div>
              <MilestoneList userId={user?.id} />
            </section>

            <section className="mt-6 rounded-2xl border border-border p-6 bg-card space-y-4">
              <div>
                <h2 className="text-lg font-semibold">Legal identity</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Used only for ID verification — never shown publicly. Must match your
                  government-issued ID, even if it differs from the name you go by.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="lfn">Legal first name</Label>
                  <Input id="lfn" maxLength={100} value={legalFirstName}
                         onChange={(e) => setLegalFirstName(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="lln">Legal last name</Label>
                  <Input id="lln" maxLength={100} value={legalLastName}
                         onChange={(e) => setLegalLastName(e.target.value)} />
                </div>
              </div>
              {legalNameChanged && wasKycVerified && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-xs text-destructive">
                  Changing your legal name will reset your ID verification. You'll need to re-verify before re-accessing verified-only features.
                </div>
              )}
            </section>

            {/* ID verification */}
            <div id="kyc" className="mt-6">
              <KycPanel />
            </div>

            {/* Trust restriction matrix — what's available, limited, paused */}
            <div className="mt-6">
              <TrustRestrictionMatrix />
            </div>

            {/* Active app */}
            <section className="mt-6 rounded-2xl border border-border p-6 bg-card space-y-4">
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-lg font-semibold">Active community</h2>
                {!isAttuned && (
                  <span className="text-xs text-muted-foreground">
                    Attuned tier unlocks all three.
                  </span>
                )}
              </div>
              <Select
                value={activeApp || undefined}
                onValueChange={(v) => setActiveApp(v as AppId)}
                disabled={!isAttuned && !!profile?.primary_app}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select community" />
                </SelectTrigger>
                <SelectContent>
                  {APPS.map((a) => (
                    <SelectItem
                      key={a.id}
                      value={a.id}
                      disabled={!isAttuned && profile?.primary_app !== a.id}
                    >
                      {a.name} — {a.community}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </section>

            {/* All-Access cross-app sync (Attuned only) */}
            <CrossAppSyncPanel isAttuned={isAttuned} />

            <section className="mt-6 rounded-2xl border border-border p-6 bg-card space-y-4">
              <h2 className="text-lg font-semibold">Notifications</h2>
              <p className="text-xs text-muted-foreground">
                Get push alerts for matches, messages, and Wavlynq announcements.
                You can manage announcement emails from the link in any broadcast.
              </p>
              <EnablePushButton />
              <div className="pt-2">
                <NotificationPreferencesPanel />
              </div>
            </section>

            {profile?.profile_id ? (
              <section className="mt-6 rounded-2xl border border-border p-6 bg-card space-y-4">
                <div>
                  <h2 className="text-lg font-semibold">Share your profile</h2>
                  <p className="text-xs text-muted-foreground">
                    Hand someone your QR code in person — they'll land on your public profile.
                  </p>
                </div>
                <ProfileQrCard
                  profileId={profile.profile_id}
                  displayName={profile.display_name}
                />
              </section>
            ) : null}

            <section className="mt-6 rounded-2xl border border-border p-6 bg-card space-y-6">
              <h2 className="text-lg font-semibold">Safety</h2>

              <div>
                <h3 className="text-sm font-semibold">Primary emergency contact</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Used as a fallback if you haven't built out your Trusted Circle.
                </p>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="en">Name</Label>
                    <Input id="en" maxLength={100} value={emergencyName}
                           onChange={(e) => setEmergencyName(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="ep">Phone</Label>
                    <Input id="ep" maxLength={30} value={emergencyPhone}
                           onChange={(e) => setEmergencyPhone(e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Incognito mode</p>
                  <p className="text-xs text-muted-foreground">Free on every tier. Hide from discovery.</p>
                </div>
                <Switch checked={incognito} onCheckedChange={setIncognito} />
              </div>

              <div>
                <h3 className="text-sm font-semibold">Trusted Circle</h3>
                <div className="mt-3">
                  <TrustedCirclePanel />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold">Safety check-ins</h3>
                <div className="mt-3">
                  <SafetyCheckinPanel />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold">Pre-meet checklist</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  60-second safety pass before any in-person meet. Includes Mapbox-curated safe venues.
                </p>
                <div className="mt-3">
                  <PreMeetChecklistPanel />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold">Who can see me</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Server-enforced visibility controls.
                </p>
                <div className="mt-3">
                  <WhoCanSeeMePanel />
                </div>
              </div>

              <div className="pt-2">
                <h3 className="text-sm font-semibold">Emergency Button</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Customize how it looks. The function is hardcoded — appearance only.
                </p>
                <div className="mt-4">
                  <EmergencyButtonSettings />
                </div>
              </div>

              <div className="pt-2">
                <h3 className="text-sm font-semibold">Auto safety share</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Default-on for events and dates created through the app.
                </p>
                <div className="mt-4">
                  <AutoSafetyShareToggle />
                </div>
              </div>
            </section>

            <div className="mt-6">
              <StrikeHistoryPanel />
            </div>


            <section className="mt-6 rounded-2xl border border-border p-6 bg-card space-y-4">
              <h2 className="text-lg font-semibold">Language</h2>
              <LocalePanel />
            </section>

            {/* Billing & refunds */}
            <section className="mt-6 rounded-2xl border border-border p-6 bg-card space-y-4">
              <h2 className="text-lg font-semibold">Billing</h2>
              <RefundsPanel />
            </section>

            <div className="mt-6">
              <AnnualUpsellCard />
            </div>

            <div className="mt-6">
              <CommunityAddOnUpsell />
            </div>

            <div className="mt-6">
              <AlacarteCrossSell />
            </div>

            {/* Privacy */}
            <section className="mt-6 rounded-2xl border border-border p-6 bg-card space-y-4">
              <h2 className="text-lg font-semibold">Privacy</h2>
              <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
                <div className="pr-4">
                  <p className="text-sm font-medium">Help fine-tune RAELA</p>
                  <p className="text-xs text-muted-foreground">
                    Off by default. When on, your classified messages join the
                    community training corpus so RAELA learns the language and
                    nuance of our community. Change anytime.
                  </p>
                </div>
                <Switch checked={aiOptIn} onCheckedChange={setAiOptIn} />
              </div>
              <p className="text-xs text-muted-foreground">
                See our <Link to="/ai-policy" className="underline hover:text-foreground">AI policy</Link> for details. RAELA's safety monitoring stays on for everyone — this toggle only controls training contributions.
              </p>

              <div className="pt-2">
                <h3 className="text-sm font-semibold">Your data rights</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Export, correct, or delete your data anytime.
                </p>
                <div className="mt-3">
                  <DsarPanel />
                </div>
              </div>
            </section>

            <div className="mt-8 flex items-center justify-between">
              <Button variant="outline" onClick={signOut}>Sign out</Button>
              <Button onClick={save} disabled={busy || usernameState === "checking"}>
                {busy ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default Account;
